import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { productService } from "../../services/productService";
import { Category, CategoryWithChildren } from "../../types";

// Define the initial state interface
interface CategoriesState {
    categories: Category[];
    categoryTree: CategoryWithChildren[];
    selectedCategory: Category | null;
    isLoading: boolean;
    isLoadingTree: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;
}

// Define the initial state
const initialState: CategoriesState = {
    categories: [],
    categoryTree: [],
    selectedCategory: null,
    isLoading: false,
    isLoadingTree: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
};

// Async thunks
export const fetchCategories = createAsyncThunk(
    "categories/fetchCategories",
    async (_, { rejectWithValue }) => {
        try {
            const categories = await productService.getCategories();
            return categories;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch categories",
            );
        }
    },
);

export const fetchCategoryById = createAsyncThunk(
    "categories/fetchCategoryById",
    async (categoryId: string, { rejectWithValue }) => {
        try {
            const category = await productService.getCategoryById(categoryId);
            return category;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch category");
        }
    },
);

export const createCategory = createAsyncThunk(
    "categories/createCategory",
    async (
        categoryData: Omit<
            Category,
            "category_id" | "create_time" | "update_time"
        >,
        { rejectWithValue, dispatch },
    ) => {
        try {
            const newCategory =
                await productService.createCategory(categoryData);
            // Refresh the categories list
            dispatch(fetchCategories());
            return newCategory;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to create category",
            );
        }
    },
);

export const updateCategory = createAsyncThunk(
    "categories/updateCategory",
    async (
        { id, data }: { id: string; data: Partial<Category> },
        { rejectWithValue, dispatch },
    ) => {
        try {
            const updatedCategory = await productService.updateCategory(
                id,
                data,
            );
            // Refresh the categories list
            dispatch(fetchCategories());
            return updatedCategory;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update category",
            );
        }
    },
);

export const fetchCategoryTree = createAsyncThunk(
    "categories/fetchCategoryTree",
    async (activeOnly: boolean = true, { rejectWithValue }) => {
        try {
            const tree = await productService.getCategoryTree(activeOnly);
            return tree;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch category tree",
            );
        }
    },
);

export const deleteCategory = createAsyncThunk(
    "categories/deleteCategory",
    async (categoryId: string, { rejectWithValue, dispatch }) => {
        try {
            await productService.deleteCategory(categoryId);
            // Refresh the categories list
            dispatch(fetchCategories());
            return categoryId;
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to delete category",
            );
        }
    },
);

// Create the categories slice
const categoriesSlice = createSlice({
    name: "categories",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setSelectedCategory: (
            state,
            action: PayloadAction<Category | null>,
        ) => {
            state.selectedCategory = action.payload;
        },
        clearSelectedCategory: (state) => {
            state.selectedCategory = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch categories
            .addCase(fetchCategories.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.isLoading = false;
                state.categories = action.payload;
                state.error = null;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch category by ID
            .addCase(fetchCategoryById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCategoryById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedCategory = action.payload;
                state.error = null;
            })
            .addCase(fetchCategoryById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create category
            .addCase(createCategory.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createCategory.fulfilled, (state) => {
                state.isCreating = false;
                state.error = null;
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.payload as string;
            })
            // Update category
            .addCase(updateCategory.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateCategory.fulfilled, (state) => {
                state.isUpdating = false;
                state.error = null;
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload as string;
            })
            // Delete category
            .addCase(deleteCategory.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteCategory.fulfilled, (state) => {
                state.isDeleting = false;
                state.error = null;
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.isDeleting = false;
                state.error = action.payload as string;
            })
            // Fetch category tree
            .addCase(fetchCategoryTree.pending, (state) => {
                state.isLoadingTree = true;
                state.error = null;
            })
            .addCase(fetchCategoryTree.fulfilled, (state, action) => {
                state.isLoadingTree = false;
                state.categoryTree = action.payload;
                state.error = null;
            })
            .addCase(fetchCategoryTree.rejected, (state, action) => {
                state.isLoadingTree = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions
export const { clearError, setSelectedCategory, clearSelectedCategory } =
    categoriesSlice.actions;

// Export selectors
export const selectCategories = (state: any) => state.categories.categories;
export const selectSelectedCategory = (state: any) =>
    state.categories.selectedCategory;
export const selectCategoriesLoading = (state: any) =>
    state.categories.isLoading;
export const selectCategoriesError = (state: any) => state.categories.error;
export const selectCategoriesCreating = (state: any) =>
    state.categories.isCreating;
export const selectCategoriesUpdating = (state: any) =>
    state.categories.isUpdating;
export const selectCategoriesDeleting = (state: any) =>
    state.categories.isDeleting;
export const selectCategoryTree = (state: any) => state.categories.categoryTree;
export const selectCategoryTreeLoading = (state: any) =>
    state.categories.isLoadingTree;

// Export reducer
export default categoriesSlice.reducer;
